import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Helper function to get ISO week number and year from a date
 * ISO weeks start on Monday and week 1 is the first week with at least 4 days in January
 * 
 * @param date - The date to calculate the ISO week for
 * @returns An object with week number and year
 */
export function getISOWeek(date: Date): { week: number; year: number } {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  const dayOfWeek = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - dayOfWeek);
  // Get first day of year
  const yearStart = new Date(d.getFullYear(), 0, 1);
  // Calculate full weeks to nearest Thursday
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  const year = d.getFullYear();
  return { week, year };
}

/**
 * Get the current ISO week number and year
 * 
 * @returns An object with current week number and year
 */
export function getCurrentWeek(): { week: number; year: number } {
  return getISOWeek(new Date());
}

/**
 * Check if a priority is overdue based on week number and year
 * A priority is considered overdue if its week/year is before the current week/year
 * 
 * @param weekNumber - The week number of the priority
 * @param weekYear - The year of the priority
 * @returns true if the priority is overdue, false otherwise
 */
export function isPriorityOverdue(weekNumber?: number, weekYear?: number): boolean {
  // If week data is not provided, priority is not considered overdue
  if (!weekNumber || !weekYear) {
    return false;
  }

  const current = getCurrentWeek();
  
  // If the year is before current year, it's overdue
  if (weekYear < current.year) {
    return true;
  }
  
  // If same year and week is before current week, it's overdue
  if (weekYear === current.year && weekNumber < current.week) {
    return true;
  }
  
  return false;
}
